import React, { useState, useEffect } from "react";
import { Eye, Upload, Loader, CheckCircle, AlertCircle, X, FileText } from 'lucide-react';
import apiService from '../../config';
import { useLanguage } from '../../StoreContext/LanguageContext';

const PaymentReceipt = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);

  // Document Viewer states
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [fileUrlToView, setFileUrlToView] = useState('');
  const [isPDFToView, setIsPDFToView] = useState(false);

  const { language, translations } = useLanguage();
  const [isLocked, setIsLocked] = useState(false);

  // Get project and proposal IDs from localStorage
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return projectData.project_id || projectData.id;
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return project.project_id || project.id;
        } catch (e) {
          console.error('Error parsing selected project:', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return user.data?.project_id || user.project_id;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      return null;
    };

    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null;
        } catch (e) {
          console.error('Error parsing proposal data:', e);
        }
      }
      return null;
    };

    const projId = getProjectInfo();
    const propId = getProposalInfo();

    setProjectId(projId);
    setProposalId(propId);

    console.log('Project ID:', projId, 'Proposal ID:', propId);
  }, []);

  // Fetch existing document when projectId and proposalId are available
  useEffect(() => {
    if (projectId && proposalId) {
      fetchExistingDocument();
    }
  }, [projectId, proposalId]);

  // Fetch existing document
  const fetchExistingDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDocuments(projectId, proposalId, 'PaymentReceipt');

      if (response.status === 200 && response.data) {
        const docData = Array.isArray(response.data) ? response.data[0] : response.data;

        if (docData.upload_path || docData.document) {
          setExistingDocument({
            document: docData.upload_path || docData.document,
            document_category: docData.document_category || 'PaymentReceipt',
            created_at: docData.created_at || new Date().toISOString()
          });
        } else {
          setExistingDocument(null);
        }
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      if (!error.message.includes('404')) {
        setError('No Document Uploaded');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('कृपया फाइल निवडा');
      return;
    }

    if (!projectId || !proposalId) {
      setError('प्रकल्प किंवा प्रस्ताव ID उपलब्ध नाही');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(false);

      const formData = new FormData();
      formData.append('type', 'PaymentReceipt');
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('document', selectedFile);

      const result = await apiService.postAddUpdateOtherDocuments(formData);
      console.log('Upload response:', result);

      const isSuccess =
        !result ||
        result.status === 200 ||
        result.status === 201 ||
        result.status === 'success' ||
        result.success === true ||
        result.data?.success === true ||
        result.data?.status === 200 ||
        result.data?.status === 201 ||
        result.message?.toLowerCase()?.includes('success') ||
        result.data?.message?.toLowerCase()?.includes('success') ||
        result.documents?.document;

      if (isSuccess) {
        setSuccess(true);
        setError(null);
        setSelectedFile(null);

        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';

        await fetchExistingDocument();

        setTimeout(() => setSuccess(false), 4000);
      } else {
        console.warn('Unexpected upload response:', result);
        setError(result?.message || 'डॉक्युमेंट अपलोड करताना त्रुटी');
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorStatus = err.response?.status;
      const errorData = err.response?.data;

      const isStillSuccess =
        errorStatus === 200 ||
        errorStatus === 201 ||
        errorData?.status === 200 ||
        errorData?.status === 201 ||
        errorData?.message?.toLowerCase()?.includes('success');

      if (isStillSuccess) {
        setSuccess(true);
        setError(null);
        setSelectedFile(null);

        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';

        await fetchExistingDocument();

        setTimeout(() => setSuccess(false), 4000);
      } else {
        setError('डॉक्युमेंट अपलोड करताना त्रुटी');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('कृपया फक्त PDF किंवा Image फाइल निवडा');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('फाइल साइज 10MB पेक्षा कमी असावी');
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleViewDocument = (urlOrFile) => {
    if (!urlOrFile) return;

    let url;
    let isPdf;

    if (urlOrFile instanceof File) {
      url = URL.createObjectURL(urlOrFile);
      isPdf = urlOrFile.type === 'application/pdf';
    } else {
      url = urlOrFile;
      isPdf = urlOrFile.toLowerCase().includes('.pdf');
    }

    setFileUrlToView(url);
    setIsPDFToView(isPdf);
    setShowDocumentViewer(true);
  };

  const closeDocumentViewer = () => {
    setShowDocumentViewer(false);
    if (fileUrlToView.startsWith('blob:')) {
      URL.revokeObjectURL(fileUrlToView);
    }
    setFileUrlToView('');
    setIsPDFToView(false);
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="flex justify-center items-center h-40">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
            <p className="text-gray-600">लोड करत आहे...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      {/* Heading */}
      <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-3">
        Payment Receipt
      </h2>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-5 h-5 mr-3" />
            <span className="font-medium">डॉक्युमेंट यशस्वीरित्या अपलोड झाले!</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-red-800">
              <AlertCircle className="w-5 h-5 mr-3" />
              <span className="font-medium">{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* File Upload Section */}
      <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              नवीन डॉक्युमेंट निवडा
            </label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-600 
                     file:mr-4 file:py-2 file:px-4 
                     file:rounded-lg file:border-0 
                     file:text-sm file:font-semibold 
                     file:bg-indigo-600 file:text-white 
                     hover:file:bg-indigo-700
                     border border-gray-300 rounded-lg
                     cursor-pointer"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-green-600">
                निवडलेली फाइल: {selectedFile.name}
              </p>
            )}
          </div>  

        {/* Action Buttons */}
        <div className="flex gap-3">
          {selectedFile && (
            <>
              <button
                onClick={() => handleViewDocument(selectedFile)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Eye className="w-4 h-4" />
                पूर्वावलोकन
              </button>

              <button
                onClick={handleSubmit}
                disabled={uploading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-colors ${uploading
                  ? 'bg-green-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
                  }`}
              >
                {uploading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    अपलोड करत आहे...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    सबमिट करा
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Existing Document Section */}
      {existingDocument && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-blue-600 mr-3" />
              <div>
                <p className="font-medium text-gray-800">सध्याचे डॉक्युमेंट</p>
                <p className="text-sm text-gray-600">अपलोड दिनांक: {new Date(existingDocument.created_at || Date.now()).toLocaleDateString('en-GB')}</p>
              </div>
            </div>
            <button
              onClick={() => handleViewDocument(existingDocument.document)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {showDocumentViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-6xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center space-x-3">
                <h3 className="text-2xl font-bold text-gray-800 tracking-tight">
                  डॉक्युमेंट पूर्वावलोकन
                </h3>
              </div>
              <button
                onClick={closeDocumentViewer}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 transition-all duration-200 flex items-center justify-center text-xl font-bold shadow-sm hover:shadow-md"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Document Viewer Container */}
            <div className="h-[calc(95vh-80px)] flex flex-col">
              <div className="flex-1 bg-gray-50 overflow-hidden shadow-inner border border-gray-200">
                <div className="w-full h-full flex items-center justify-center">
                  {isPDFToView ? (
                    <iframe
                      src={fileUrlToView}
                      className="w-full h-full border-0 bg-white shadow-lg"
                      title="PDF Preview"
                    />
                  ) : (
                    <img
                      src={fileUrlToView}
                      alt="Document"
                      className="max-w-full max-h-full object-contain shadow-lg"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReceipt;